import {
  CustomEventTriggerStep,
  flowCategoriesWithSkipConditions,
  TriggerStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { RuleTriggerType } from "@redotech/redo-model/advanced-flow/rules/types";
import { SchemaType } from "@redotech/redo-model/advanced-flow/schemas/schemas";
import {
  CustomEventTriggerKey,
  getTriggerDisplayName,
  MarketingTriggerKey,
  rulesTriggers,
  rulesV3Triggers,
} from "@redotech/redo-model/advanced-flow/triggers";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMemo } from "react";
import { getTriggerIconForSchemaType } from "../nodes/trigger-node";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../steps";
import { expressionIsValid } from "./condition";
import { BackInStockFieldSelector } from "./trigger-steps/back-in-stock-trigger-step";
import { CustomEventTriggerInspector } from "./trigger-steps/custom-event-trigger-step";
import {
  DateFieldSelector,
  dateTriggerStepIsValid,
} from "./trigger-steps/marketing-date-trigger-step";
import {
  PriceDropFieldSelector,
  priceDropTriggerStepIsValid,
} from "./trigger-steps/marketing-price-drop-trigger-step";
import { ShouldSkipSmartSendingInspector } from "./trigger-steps/should-skip-smart-sending-inspector";
import { SkipConditionsInspector } from "./trigger-steps/skip-conditions-inspector";
import { WorkOrderTriggerInspector } from "./work-order/work-order-trigger-inspector";

export const triggerStepType: FrontendStepType<
  PartialWithRequiredType<TriggerStep>
> = {
  valid: (state) => valid(state),
  selectable: (_) => true,
  type: "custom",
  customInspector: TriggerInspector,
};

function valid(state: PartialWithRequiredType<TriggerStep>) {
  if (!validateSkipConditions(state)) {
    return false;
  }
  if (state.key === MarketingTriggerKey.DATE) {
    return dateTriggerStepIsValid(state);
  }

  if (state.key === MarketingTriggerKey.PRICE_DROP) {
    return priceDropTriggerStepIsValid(state);
  }
  return true;
}

function validateSkipConditions(state: PartialWithRequiredType<TriggerStep>) {
  if (!state.skipConditions) {
    return true;
  }
  if (
    !state.skipConditions.conditions ||
    !state.skipConditions.conjunctionMode
  ) {
    return false;
  }
  return state.skipConditions.conditions?.every((condition) =>
    expressionIsValid(condition),
  );
}

function TriggerInspector(
  props: StepTypeDetailsProps<PartialWithRequiredType<TriggerStep>>,
) {
  const key = props.step.key;
  const headerSection = <TriggerInspectorHeader {...props} />;
  const shouldSkipSmartSendingSection = (
    <ShouldSkipSmartSendingInspector {...props} />
  );
  const skipConditionsSection = <SkipConditionsInspector {...props} />;

  let triggerSpecificContent = null;
  switch (key) {
    case MarketingTriggerKey.DATE: {
      triggerSpecificContent = (
        <DateFieldSelector
          value={props.step}
          valueChange={(value) => {
            props.setStep(value);
          }}
        />
      );
      break;
    }
    case MarketingTriggerKey.PRICE_DROP: {
      triggerSpecificContent = (
        <PriceDropFieldSelector
          value={props.step}
          valueChange={(value) => {
            props.setStep(value);
          }}
        />
      );
      break;
    }
    case MarketingTriggerKey.BACK_IN_STOCK: {
      triggerSpecificContent = (
        <BackInStockFieldSelector
          value={props.step}
          valueChange={(value) => {
            props.setStep(value);
          }}
        />
      );
      break;
    }
    case CustomEventTriggerKey.CUSTOM_EVENT: {
      triggerSpecificContent = (
        <CustomEventTriggerInspector
          setStep={(updated) =>
            props.setStep(updated as PartialWithRequiredType<TriggerStep>)
          }
          step={props.step as PartialWithRequiredType<CustomEventTriggerStep>}
        />
      );
      break;
    }
  }
  if (props.step.schemaType === SchemaType.WORK_ORDER) {
    return (
      <Flex dir="column" gap="xl" pt="xl" w="full">
        {headerSection}
        <WorkOrderTriggerInspector
          automation={props.automation}
          setAutomation={props.setAutomation}
          setTriggerConditions={(value) => {
            props.setAutomation?.((prev) =>
              prev ? { ...prev, triggerConditions: value } : prev,
            );
          }}
          triggerConditions={props.triggerConditions}
        />
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="3xl" pt="xl" w="full">
      {headerSection}
      {shouldSkipSmartSendingSection}
      {triggerSpecificContent}
      {props.step.category &&
        flowCategoriesWithSkipConditions.includes(props.step.category) &&
        skipConditionsSection}
    </Flex>
  );
}

function TriggerInspectorHeader(
  props: StepTypeDetailsProps<PartialWithRequiredType<TriggerStep>>,
) {
  const Icon = getTriggerIconForSchemaType(props.step.schemaType);

  const isV3 = props.step.schemaType === SchemaType.RULES_V3;
  const ruleTriggerOptions: RedoListItem<RuleTriggerType>[] = useMemo(() => {
    const triggers = isV3 ? rulesV3Triggers : rulesTriggers;
    return triggers.map((trigger) => ({
      id: trigger.key,
      type: "text" as const,
      text: trigger.displayName,
      value: trigger.key,
      description: trigger.description,
    }));
  }, [isV3]);

  const selectedItem = useMemo(() => {
    return ruleTriggerOptions.find((opt) => opt.value === props.step.key);
  }, [ruleTriggerOptions, props.step.key]);

  return (
    <Flex dir="column" gap="md" w="full">
      <Flex align="center" justify="flex-start">
        <RedoFeaturedIcon
          color="black"
          Icon={Icon}
          size="sm"
          type="high-contrast"
        />
        <Text fontSize="sm" fontWeight="medium" textColor="primary">
          {props.step.schemaType === SchemaType.WORK_ORDER
            ? "Work order flow"
            : (getTriggerDisplayName(props.step.key) ?? "")}
        </Text>
      </Flex>
      {props.step.category === "Rules" && (
        <RedoSingleSelectDropdownInput<RuleTriggerType>
          fitToAnchor
          label="Trigger type"
          options={ruleTriggerOptions}
          optionSelected={({ item }) => {
            props.setStep({
              ...props.step,
              key: item.value,
              category: "Rules",
              schemaType: isV3 ? SchemaType.RULES_V3 : SchemaType.RULES,
            });
          }}
          placeholder="Select trigger type"
          selectedItem={selectedItem}
        />
      )}
    </Flex>
  );
}
